/**
 * Singleton presence state for AI agents (MCP clients) talking to the
 * control-plane on behalf of the logged-in user.
 *
 * Shared across every component that calls `useAiPresence()` — the
 * pill widget, the global banner, anything else later. Only one
 * polling timer runs no matter how many consumers there are.
 *
 * Follow-mode state also lives here so the pill (in the page header)
 * and the banner (mounted in App.vue) stay in sync.
 *
 * Lifecycle: lazy-started on the first `useAiPresence()` call. Never
 * stopped — even when no component is mounted — so navigating between
 * routes doesn't cause re-init churn.
 */

import axios from "axios"
import { computed, ref, shallowRef, watch } from "vue"
import { useRoute, useRouter } from "vue-router"

export interface AiActivity {
    label: string | null
    url: string
}

export interface AiSession {
    mcpSessionId: string
    firstSeen: string
    lastSeen: string
    client: string | null
    activity: AiActivity | null
}

export interface AiPresenceResponse {
    active: boolean
    sessionsCount: number
    lastActivityAt: string | null
    freshnessSeconds: number
    sessions: AiSession[]
}

const POLL_INTERVAL_MS = 10_000

// ── singleton state ───────────────────────────────────────────────

const active = ref(false)
const sessionsCount = ref(0)
const lastActivityAt = ref<string | null>(null)
const sessions = shallowRef<AiSession[]>([])
const hasNewSession = ref(false)
const error = ref<unknown>(null)

const followingSessionId = ref<string | null>(null)

const followedSession = computed<AiSession | null>(() => {
    if (!followingSessionId.value) return null
    return sessions.value.find(s => s.mcpSessionId === followingSessionId.value) ?? null
})
const isFollowing = computed(() => followingSessionId.value !== null)
const followedSessionActive = computed(() => followedSession.value !== null)

let knownSessionIds = new Set<string>()
let timer: number | null = null
let routerWatchersInstalled = false

async function fetchOnce() {
    try {
        const resp = await axios.get<AiPresenceResponse>(
            "/api/v1/ai-presence/current",
        )
        const data = resp.data
        active.value = data.active
        sessionsCount.value = data.sessionsCount
        lastActivityAt.value = data.lastActivityAt
        sessions.value = data.sessions || []

        const currentIds = new Set((data.sessions || []).map(s => s.mcpSessionId))
        if (knownSessionIds.size > 0) {
            for (const id of currentIds) {
                if (!knownSessionIds.has(id)) {
                    hasNewSession.value = true
                    break
                }
            }
        }
        knownSessionIds = currentIds
        error.value = null
    } catch (e) {
        error.value = e
    }
}

function startPolling() {
    if (timer !== null) return
    fetchOnce()
    timer = window.setInterval(fetchOnce, POLL_INTERVAL_MS)
}

// ── follow-mode helpers ───────────────────────────────────────────

let _router: ReturnType<typeof useRouter> | null = null

/** Path part of a URL, normalised for comparison (no query, no hash,
 *  no trailing slash). */
function pathOf(url: string | null | undefined): string {
    if (!url) return ""
    return url.split("?")[0].split("#")[0].replace(/\/+$/, "")
}

function navigateFollow(url: string) {
    if (!_router) return
    if (pathOf(url) === pathOf(_router.currentRoute.value.fullPath)) return
    _router.push(url).catch(() => { /* duplicate / aborted nav — ignore */ })
}

function installRouterWatchersOnce() {
    if (routerWatchersInstalled) return
    if (!_router) return
    routerWatchersInstalled = true

    const route = _router.currentRoute

    // Auto-navigate when the followed agent moves to a new page.
    watch(
        () => followedSession.value?.activity?.url ?? null,
        (newUrl, oldUrl) => {
            if (!newUrl || newUrl === oldUrl) return
            navigateFollow(newUrl)
        },
    )

    // Exit follow-mode when the user navigates away from where the agent
    // is. Robust state comparison (no push-counting): if we're following
    // and the current route no longer matches the agent's current
    // activity page, the user must have moved on their own.
    //
    //  - Agent moves → activity.url updates → we push → route ends up
    //    matching activity.url → we stay following.
    //  - User clicks elsewhere → route stops matching activity.url →
    //    we stop following.
    //  - Agent has no activity url (disconnected / never started) → we
    //    don't auto-stop; the banner shows the disconnected state until
    //    the user dismisses it.
    watch(
        () => route.value.fullPath,
        (newPath) => {
            if (!isFollowing.value) return
            const agentUrl = followedSession.value?.activity?.url
            if (!agentUrl) return
            if (pathOf(newPath) === pathOf(agentUrl)) return
            followingSessionId.value = null
        },
    )
}

// ── public API ────────────────────────────────────────────────────

export function useAiPresence() {
    // Lazy-acquire the router from the first caller. After that, both
    // the pill and the banner share the same router instance and
    // watchers.
    if (!_router) {
        try {
            _router = useRouter()
        } catch {
            // useRouter() needs an active component scope. If called
            // outside a setup (unlikely here), just skip — the next
            // valid caller will install it.
        }
    }
    installRouterWatchersOnce()
    startPolling()

    return {
        // presence
        active,
        sessionsCount,
        lastActivityAt,
        sessions,
        hasNewSession,
        error,
        ack() { hasNewSession.value = false },
        refresh: fetchOnce,
        // follow-mode
        followingSessionId,
        followedSession,
        isFollowing,
        followedSessionActive,
        startFollowing(s: AiSession) {
            followingSessionId.value = s.mcpSessionId
            if (s.activity?.url) navigateFollow(s.activity.url)
        },
        stopFollowing() {
            followingSessionId.value = null
        },
        toggleFollow(s: AiSession) {
            if (followingSessionId.value === s.mcpSessionId) {
                followingSessionId.value = null
            } else {
                followingSessionId.value = s.mcpSessionId
                if (s.activity?.url) navigateFollow(s.activity.url)
            }
        },
    }
}
